import Head from 'next/head';
import React, { FC } from 'react';
import { substring } from 'runes2';

import { useTranslation } from 'next-i18next';
import { FullBoatSearchDetail, type RelatedAndListingCardBoatMinimal } from '../../../common/types/api/v4/boats/search';
import DescriptionMetaTag from '../../../common/components/MetaTags/DescriptionMetaTag';
import BoatSchema from '../../../common/components/MetaTags/BoatSchema';
import OpenGraphMetaTags from '../../../common/components/MetaTags/OpenGraphMetaTags';

export type MetaFieldsProps = {
  listing: FullBoatSearchDetail;
  relatedListings: RelatedAndListingCardBoatMinimal[];
};

const MetaFields: FC<MetaFieldsProps> = ({
  listing,
  relatedListings,
}) => {
  const [t] = useTranslation('listing-page');
  const {
    headline,
    title: seoTitle,
    meta_description: metaDescription,
    search_price_nested: {
      formatted: priceFormatted,
      type: priceType,
      minimum: priceMinimumQuantity,
    },
    city,
    state,
    category_string: categoryString,
    capacity,
    year,
    max_length: maxLength,
    captained,
    rating_aggregates: {
      rating_average: ratingAverage,
      total_reviews: totalReviews,
    },
    photos,
    total_successful_bookings: totalSuccessfulBookings,
  } = listing;

  const priceTypeLabels = {
    hourly_price: { single: t('meta-description--hourly_one'), plural: t('meta-description--hourly_other') },
    daily_price: { single: t('meta-description--daily_one'), plural: t('meta-description--daily_other') },
    nightly_price: { single: t('meta-description--nightly_one'), plural: t('meta-description--nightly_other') },
    person_price: { single: t('meta-description--person_one'), plural: t('meta-description--person_other') },
    weekly_price: { single: t('meta-description--weekly_one'), plural: t('meta-description--weekly_other') },
  };

  // If seoTitle is provided, use it as the title
  const title = seoTitle || headline;

  // If meta_description is provided, use it as the description
  let description = metaDescription;

  // If meta_description is not provided, use the concatenated string below
  if (!description) {
    description = `
      ${headline.length > 50 ? `${substring(headline.toUpperCase(), 0, 50)}...` : headline.toUpperCase()},
      ${t('meta-description--rate')}: ${priceFormatted} / ${priceTypeLabels[priceType].single},
      ${priceMinimumQuantity > 1 ? `${t('meta-description--minimum')}: ${priceMinimumQuantity} ${priceTypeLabels[priceType].plural}, ` : ''}

      ${t('meta-description--location')}: ${city}, ${state},
      ${categoryString.length ? `${t('meta-description--type')}: ${categoryString},` : ''}
      ${t('meta-description--capacity')}: ${capacity},
      ${year ? `${t('meta-description--year')}: ${year},` : ''}
      ${maxLength ? `${t('meta-description--length')}: ${maxLength}ft,` : ''}
      ${t('meta-description--captain')}: ${captained ? t('meta-description--included') : t('meta-description--not-included')},
      ${ratingAverage ? `${t('meta-description--rating')}: ${ratingAverage} / 5 ${t('meta-description--stars')} (${totalReviews} ${t('meta-description--reviews')}),` : ''}
      ${t('meta-description--photos')}: ${photos.length}
      ${totalSuccessfulBookings ? `, ${t('meta-description--bookings')}: ${totalSuccessfulBookings}` : ''}`;
    description = description.replace(/\s{2,}/gm, ' ').trim();
  }
  return (
    <>
      <Head>
        <title>
          {title}
        </title>
      </Head>

      <DescriptionMetaTag content={description} />

      <OpenGraphMetaTags
        canonicalPath={listing.listing_url}
        title={title}
        imageUrl={listing.photo_url}
        description={description}
      />

      <BoatSchema
        listing={listing}
        relatedListings={relatedListings}
      />
    </>
  );
};

export default MetaFields;
